/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 * ============================================================================= */

// Per swa #65 + AI-LAUNCH-22 — production path is HttpOnly cookie issued by
// auth-svc on the parent domain (e.g. `.paperboat.skyxops.io`). The browser
// auto-attaches the cookie to every same-site request when fetch is called
// with `credentials: 'include'`.
//
// Two fallback paths are kept for backward compatibility:
//   1. Authorization: Bearer header — used by header-based clients and tests,
//      and when running launcher on plain-HTTP localhost where the cookie's
//      Domain attribute does not match the test origin.
//   2. URL `?token=` parameter — DEV-ONLY, gated by the build-time
//      `__ALLOW_URL_TOKEN__` flag (vite.config.ts). Never enabled in prod.
//
// The in-memory `currentToken` is set by LoginPage after mockLogin returns
// (for the header fallback). In production, the cookie carries the session
// and `currentToken` may remain null — both `credentials: 'include'` and the
// CSRF header will handle auth.

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
// AI-AUTH-10 — per-env cookie-name suffix from the host so an apex/prod cookie
// (Domain=.skyxops.io, also sent to *.{env}.skyxops.io) can't shadow this env's
// same-named CSRF cookie. Apex {app}.skyxops.io → ''; {app}.{env}.skyxops.io → '_{env}'.
function cookieSuffix(): string {
  if (typeof window === 'undefined') return '';
  const h = window.location.hostname.toLowerCase();
  const dotbase = '.skyxops.io';
  if (!h.endsWith(dotbase)) return '';
  const labels = h.slice(0, -dotbase.length).split('.').filter(Boolean);
  return labels.length <= 1 ? '' : '_' + labels.slice(1).join('_');
}
const CSRF_COOKIE_NAME = 'skyxops_csrf' + cookieSuffix();
const CSRF_HEADER_NAME = 'X-CSRF-Token';

declare const __ALLOW_URL_TOKEN__: boolean;

let currentToken: string | null = null;

export function setToken(token: string | null): void {
  currentToken = token;
}

export function getToken(): string | null {
  return currentToken;
}

function readCookie(name: string): string | null {
  // Lightweight cookie reader — no dependency on a cookie lib.
  const target = name + '=';
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(target)) {
      return decodeURIComponent(trimmed.slice(target.length));
    }
  }
  return null;
}

function readUrlTokenIfAllowed(): string | null {
  if (typeof __ALLOW_URL_TOKEN__ === 'undefined' || !__ALLOW_URL_TOKEN__) {
    return null;
  }
  const url = new URL(window.location.href);
  return url.searchParams.get('token');
}

/**
 * Remove query parameters from the address bar without a navigation.
 *
 * Credentials that arrive in a URL — a session token, an emailed reset code, an
 * invite token — otherwise persist in the address bar and browser history for
 * the life of the page, and can ride an outbound Referer. Capture the value into
 * state, then scrub it (AI-LAUNCH-45).
 */
export function scrubUrlParams(...names: string[]): void {
  const cleaned = new URL(window.location.href);
  let changed = false;
  for (const name of names) {
    if (cleaned.searchParams.has(name)) {
      cleaned.searchParams.delete(name);
      changed = true;
    }
  }
  if (changed) window.history.replaceState({}, '', cleaned.toString());
}

// On module load, if URL has a token AND the build allows it, populate
// the in-memory token so subsequent requests can attach Authorization.
const urlToken = readUrlTokenIfAllowed();
if (urlToken) {
  currentToken = urlToken;
  scrubUrlParams('token');
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string>),
  };
  if (currentToken) {
    headers['Authorization'] = `Bearer ${currentToken}`;
  }
  const method = (init?.method ?? 'GET').toUpperCase();
  if (MUTATING_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE_NAME);
    if (csrf) {
      headers[CSRF_HEADER_NAME] = csrf;
    }
  }
  const resp = await fetch(path, { ...init, headers, credentials: 'include' });
  if (!resp.ok) {
    let detail: string;
    try {
      const body = await resp.json();
      detail = body.detail ?? JSON.stringify(body);
    } catch {
      detail = resp.statusText;
    }
    throw new ApiError(resp.status, detail);
  }
  // 204 No Content (e.g. confirm-forgot-password, logout) has an empty body —
  // don't try to JSON-parse it ("Unexpected end of JSON input").
  if (resp.status === 204) {
    return undefined as T;
  }
  const text = await resp.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// ---------------------------------------------------------------------------
// Endpoints (typed)
// ---------------------------------------------------------------------------
// Email + password login (AI-LAUNCH-24). The backend verifies the password
// against the Stratos user tables and forwards the session cookie. A 401
// ApiError means bad credentials.
export interface LoginResponse {
  token?: string;
  expires_at?: number;
  user_id?: string;
  user_principal_type?: string;
  redirect_to?: string;
  // When MFA (TOTP) is enrolled, the broker asks for a code instead of a token.
  mfa_required?: boolean;
  session?: string;
}

export function login(
  email: string,
  password: string,
  opts?: { mfaCode?: string; session?: string },
): Promise<LoginResponse> {
  return request<LoginResponse>('/api/v1/launcher/login', {
    method: 'POST',
    body: JSON.stringify({
      email,
      password,
      mfa_code: opts?.mfaCode,
      session: opts?.session,
    }),
  });
}

// Password reset (AI-AUTH-7 phase 3) — Cognito ForgotPassword / Confirm.
export function forgotPassword(email: string): Promise<void> {
  return request<void>('/api/v1/launcher/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return request<void>('/api/v1/launcher/confirm-forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email, code, new_password: newPassword }),
  });
}

// Accept-invite (AI-MSP-109) — secure staff onboarding. The invitee opens the
// emailed link, we validate the token, then they set their own password.
export interface InviteInfo {
  email: string;
  kind?: 'staff' | 'client';
  org_name?: string | null;
  // staff invites
  msp_id?: number;
  msp_name?: string | null;
  // client invites
  client_id?: number;
  company_name?: string | null;
}

export function checkInvite(token: string): Promise<InviteInfo> {
  return request<InviteInfo>(
    `/api/v1/launcher/accept-invite?token=${encodeURIComponent(token)}`,
  );
}

export function acceptInvite(
  token: string,
  password: string,
  displayName?: string,
): Promise<{ ok: boolean; email: string }> {
  return request('/api/v1/launcher/accept-invite', {
    method: 'POST',
    body: JSON.stringify({ token, password, display_name: displayName }),
  });
}

// Accept-invite via SSO is a redirect to auth-svc /sso/start?invite=<token>
// (handled in AcceptInvitePage) — no API call here; the SSO callback finalizes
// the account only after verifying the federated email matches the invite.

// ---------------------------------------------------------------------------
// TOTP MFA enrollment (AI-AUTH-8). auth-svc owns Cognito but exposes no CORS, so
// the browser cannot call it cross-origin. We go same-origin through the launcher
// backend, which proxies to auth-svc (forwarding the session + mfa_tx cookies and
// Set-Cookie). Reuses the standard request() helper — same CSRF + 204 handling.
export function mfaStatus(): Promise<{ enabled: boolean }> {
  return request('/api/v1/launcher/mfa/status');
}

export function mfaEnrollStart(password: string): Promise<{ secret: string; otpauth_uri: string }> {
  return request('/api/v1/launcher/mfa/enroll/start', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export function mfaEnrollVerify(code: string): Promise<{ ok: boolean }> {
  return request('/api/v1/launcher/mfa/enroll/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export function mfaDisable(password: string): Promise<{ ok: boolean }> {
  return request('/api/v1/launcher/mfa/disable', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

// ---------------------------------------------------------------------------
// Account / profile (AI-AUTH-7). Same-origin proxy to auth-svc (it owns identity).
export interface Profile {
  user_id: string;
  email: string | null;
  display_name: string | null;
  time_zone: string | null;
  locale: string | null;
  theme_preference: 'light' | 'dark' | 'system' | null;
  is_federated: boolean;
  email_confirmed: boolean;
  sso_connected?: boolean;
  sso_provider?: string | null;
  has_password?: boolean;
  user_principal_type: 'client_user' | 'msp_user' | 'admin';
}

export function getProfile(): Promise<Profile> {
  return request<Profile>('/api/v1/launcher/profile');
}

export function disconnectSso(): Promise<Profile> {
  return request<Profile>('/api/v1/launcher/profile/disconnect-sso', { method: 'POST' });
}

export function updateProfile(
  patch: Partial<Pick<Profile, 'display_name' | 'time_zone' | 'locale' | 'theme_preference'>>,
): Promise<Profile> {
  return request<Profile>('/api/v1/launcher/profile', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: boolean }> {
  return request('/api/v1/launcher/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
}

export function logout(): Promise<void> {
  // Clears the auth cookies server-side. After this resolves, the in-memory
  // token is also cleared so the header path is consistent.
  const promise = request<unknown>('/api/v1/launcher/logout', { method: 'POST' }).then(
    () => {
      currentToken = null;
    },
    () => {
      // 204 has no body — `request<unknown>` may throw when JSON parsing fails.
      // Treat as success and clear the token regardless.
      currentToken = null;
    },
  );
  return promise as Promise<void>;
}

export interface Tile {
  app_id: string;
  name: string;
  route_prefix: string;
  image: string | null;
}

export interface TilesResponse {
  principal: {
    user_id: string;
    display_name?: string | null;
    user_principal_type: 'client_user' | 'msp_user' | 'admin';
    client_id: number | null;
    msp_id: number | null;
    acting_as_client_id: number | null;
  };
  tiles: Tile[];
}

export function getTiles(): Promise<TilesResponse> {
  return request<TilesResponse>('/api/v1/launcher/tiles');
}

/**
 * Friendly display name for a principal. Prefers a non-blank `display_name`,
 * else the email local-part (before `@`) of `user_id`, else 'there' — so the UI
 * never surfaces a raw email/user_id as the user's name. `||` (not `??`) so an
 * empty-string display_name also falls through.
 */
export function principalDisplayName(p: {
  display_name?: string | null;
  user_id: string;
}): string {
  return (
    p.display_name?.trim() || p.user_id.split('@')[0]?.trim() || 'there'
  );
}

export interface MeResponse {
  user_id: string;
  user_principal_type: 'client_user' | 'msp_user' | 'admin';
  client_id: number | null;
  msp_id: number | null;
  acting_as_client_id: number | null;
  effective_client_id: number | null;
  exp: number;
}

export function whoami(): Promise<MeResponse> {
  return request<MeResponse>('/api/v1/launcher/me');
}

export interface Branding {
  app_name: string;
  eyebrow_text: string | null;
  primary_color: string;
  logo_blob_id: string | null;
  hide_skyxops_branding: boolean;
  source: 'skyxops' | 'client' | 'msp' | 'msp+client';
}

export function getBranding(): Promise<Branding> {
  return request<Branding>('/api/v1/launcher/branding');
}

// --- AWS Marketplace registration (AI-LAUNCH-59) -----------------------------
export interface MarketplaceSession {
  resolved: boolean;
  product_code?: string | null;
  free_trial?: boolean;
}

/** What the backend resolved from the AWS registration token. `resolved:false`
 *  is the ordinary answer for a visitor who did not arrive from AWS - the page
 *  renders an inert message rather than a signup form. */
export async function marketplaceSession(): Promise<MarketplaceSession> {
  return request<MarketplaceSession>('/api/v1/launcher/marketplace/session');
}

export interface MarketplaceCompleteBody {
  email: string;
  company: string;
  full_name: string;
}

export async function marketplaceComplete(
  body: MarketplaceCompleteBody,
): Promise<{ ok: boolean; client_id: number }> {
  return request('/api/v1/launcher/marketplace/complete', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
